import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { FONT, T, originsFor, focusRing } from "./tokens";
import { useSectionBridge } from "./SectionBridge";

// ===========================================================================
// CompareOverlay — before/after slider. Two independently-bridged iframes of
// the same site load side by side; a draggable divider clips the "after" one
// so you can wipe between two edit sets. Used when a VersionChip is held.
// ===========================================================================

function rebuild(cmd, edits) {
  cmd.revertAll();
  (edits || []).forEach((e) => cmd.applyEdit(e.section, e.op));
}

function BridgedFrame({ src, allowlist, edits, title, clip }) {
  const ref = useRef(null);
  const bridge = useSectionBridge({ iframeRef: ref, allowlist });
  useEffect(() => {
    if (bridge.ready) rebuild(bridge, edits);
  }, [bridge.ready]);
  return (
    <div style={{ position: "absolute", inset: 0, clipPath: clip, overflow: "hidden" }}>
      <iframe
        ref={ref}
        src={src}
        title={title}
        style={{ width: "100%", height: "100%", border: "none", display: "block", background: "#fff" }}
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
}

export default function CompareOverlay({ demoUrl, beforeEdits, afterEdits, beforeLabel = "Current", afterLabel = "This version", onClose }) {
  const [pos, setPos] = useState(50); // % divider position
  const wrapRef = useRef(null);
  const dragging = useRef(false);
  const allowlist = originsFor(demoUrl);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      setPos(Math.max(2, Math.min(98, (x / r.width) * 100)));
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Compare versions"
      style={{ position: "fixed", inset: 0, zIndex: 1600, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: FONT }}
    >
      <div style={{ position: "relative", width: "min(1100px, 94vw)", height: "min(760px, 86vh)", borderRadius: T.radiusFrame, overflow: "hidden", background: "#fff", boxShadow: T.shadowLg }}>
        <div ref={wrapRef} style={{ position: "absolute", inset: 0, userSelect: "none" }}>
          {/* Before (full) */}
          <BridgedFrame src={demoUrl} allowlist={allowlist} edits={beforeEdits} title={`${beforeLabel} preview`} clip="none" />
          {/* After (clipped to the left of the divider) */}
          <BridgedFrame src={demoUrl} allowlist={allowlist} edits={afterEdits} title={`${afterLabel} preview`} clip={`inset(0 ${100 - pos}% 0 0)`} />

          {/* Labels */}
          <span style={{ position: "absolute", top: 14, left: 14, zIndex: 5, padding: "4px 10px", borderRadius: T.radiusFull, background: T.blue, color: "#fff", fontSize: 11.5, fontWeight: 700 }}>{afterLabel}</span>
          <span style={{ position: "absolute", top: 14, right: 14, zIndex: 5, padding: "4px 10px", borderRadius: T.radiusFull, background: "rgba(15,23,42,0.72)", color: "#fff", fontSize: 11.5, fontWeight: 700 }}>{beforeLabel}</span>

          {/* Divider handle */}
          <div
            role="slider"
            aria-label="Comparison position"
            aria-valuenow={Math.round(pos)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onMouseDown={() => (dragging.current = true)}
            onTouchStart={() => (dragging.current = true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") setPos((p) => Math.max(2, p - 3));
              if (e.key === "ArrowRight") setPos((p) => Math.min(98, p + 3));
            }}
            style={{ position: "absolute", top: 0, bottom: 0, left: `${pos}%`, transform: "translateX(-50%)", width: 32, cursor: "ew-resize", zIndex: 6, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <span style={{ position: "absolute", top: 0, bottom: 0, width: 2, background: "#fff", boxShadow: "0 0 0 1px rgba(15,23,42,0.15)" }} />
            <span style={{ width: 34, height: 34, borderRadius: "50%", background: "#fff", border: `1px solid ${T.line}`, boxShadow: T.shadowMd, display: "flex", alignItems: "center", justifyContent: "center", color: T.ink2, fontSize: 13, fontWeight: 700 }}>⇔</span>
          </div>
        </div>

        <button
          type="button"
          aria-label="Close comparison"
          onClick={onClose}
          onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)}
          onBlur={(e) => (e.currentTarget.style.boxShadow = T.shadowMd)}
          style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 8, width: 32, height: 32, borderRadius: T.radiusFull, border: `1px solid ${T.line}`, background: "#fff", color: T.ink2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: T.shadowMd }}
        >
          <X size={15} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
